import { Assessment, Close, Edit } from '@mui/icons-material';
import Card, { CardProps } from '../general/Card';
import React from 'react';
import { REPORT_COLORS } from './constants';
import WhiteFab from '../common/WhiteFab';
import { format } from 'common/utils/common.util';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ReportGeneralInfo, ReportStatusEnum } from './report.type';
import { Tooltip } from '@mui/material';
import NumericFormatInput from '../general/NumericFormatInput';
import { Button } from 'components/index';
import { TOOLTIPS } from './report.util';
import { getErrorFieldNames } from '../project/project.util';
import { FileUploadErrorDTO } from '../project/project.type';
import StyledTextField from '../project/StyledTextField';

type GeneralInfoCardProps = {
  generalInfo: any;
  updateGeneralInfo: (generalInfo: any) => void;
  isLocked?: boolean;
  reportStatus?: string;
  cardProps?: Partial<CardProps>;
  projectErrors?: FileUploadErrorDTO[];
};

const GeneralInfoCard: React.FC<GeneralInfoCardProps> = ({
  generalInfo,
  updateGeneralInfo,
  isLocked = false,
  reportStatus,
  cardProps,
  projectErrors,
}) => {
  const [isEditMode, setIsEditMode] = React.useState(false);
  const toggleEditMode = () => setIsEditMode(val => !val);
  const errorFieldNames = getErrorFieldNames(projectErrors || []);

  // reset if props change
  React.useEffect(() => {
    if (isEditMode) setIsEditMode(false);
  }, [generalInfo, isLocked]);

  const initialOrDefaultValues: ReportGeneralInfo = {
    reportName: generalInfo?.reportName ?? '',
    grossBuildingSF: generalInfo?.grossBuildingSF,
    netRentableSF: generalInfo?.netRentableSF,
    siteAcreage: generalInfo?.siteAcreage,
    unitCount: generalInfo?.unitCount,
    buildingCount: generalInfo?.buildingCount,
    storyCount: generalInfo?.storyCount,
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      grossBuildingSF: yup.number().nullable().min(0, 'Must not be negative'),
      netRentableSF: yup.number().nullable().min(0, 'Must not be negative'),
      siteAcreage: yup.number().nullable().min(0, 'Must not be negative'),
      unitCount: yup.number().nullable().min(0, 'Must not be negative'),
      buildingCount: yup.number().nullable().min(0, 'Must not be negative'),
      storyCount: yup.number().nullable().min(0, 'Must not be negative'),
    }),
    onSubmit: values => {
      updateGeneralInfo(values);
      setIsEditMode(false);
    },
  });

  const canSubmit = React.useMemo(() => {
    if (isLocked) return false;
    if (!formik.isValid) return false;
    return formik.dirty;
  }, [formik, isLocked]);

  const handleCancel = () => {
    formik.resetForm();
    setIsEditMode(false);
  };

  return (
    <form onSubmit={formik.handleSubmit} style={{ height: '100%' }}>
      <Card
        itemsPerRow={cardProps?.itemsPerRow ?? 1}
        icon={cardProps?.icon ?? <Assessment color='primary' />}
        heading={cardProps?.heading ?? 'General Info'}
        backgroundColor={generalInfo?.isEdited ? REPORT_COLORS.UNSAVED_BACKGROUND_COLOR : undefined}
        headerAction={
          isEditMode ? (
            <WhiteFab aria-label='cancel' icon={<Close fontSize='small' />} textColor='error' size='small' onClick={handleCancel} />
          ) : (
            <Tooltip
              title={
                isLocked && reportStatus === ReportStatusEnum.Completed ? TOOLTIPS.statusCompleted : isLocked ? TOOLTIPS.unlockToEdit : ''
              }
              arrow={false}
            >
              <span>
                <WhiteFab
                  aria-label='edit'
                  icon={<Edit fontSize='small' />}
                  textColor='primary'
                  size='small'
                  disabled={isLocked}
                  onClick={toggleEditMode}
                />
              </span>
            </Tooltip>
          )
        }
        footer={
          isEditMode ? (
            <Button disabled={!canSubmit} onClick={formik.submitForm} sx={{ marginLeft: 'auto' }}>
              Save Draft
            </Button>
          ) : undefined
        }
        data={
          isEditMode
            ? {
                'Gross Building SF': (
                  <StyledTextField
                    name='grossBuildingSF'
                    variant='standard'
                    value={formik.values.grossBuildingSF}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.grossBuildingSF && Boolean(formik.errors.grossBuildingSF)}
                    helperText={formik.touched.grossBuildingSF && formik.errors.grossBuildingSF}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    shouldHighlight={errorFieldNames?.includes('Gross Building SF') ?? false}
                  />
                ),
                'Net Rentable SF': (
                  <StyledTextField
                    name='netRentableSF'
                    variant='standard'
                    value={formik.values.netRentableSF}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.netRentableSF && Boolean(formik.errors.netRentableSF)}
                    helperText={formik.touched.netRentableSF && formik.errors.netRentableSF}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    shouldHighlight={errorFieldNames?.includes('Net Rentable SF') ?? false}
                  />
                ),
                '# Apt Buildings': (
                  <StyledTextField
                    name='buildingCount'
                    variant='standard'
                    value={formik.values.buildingCount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.buildingCount && Boolean(formik.errors.buildingCount)}
                    helperText={formik.touched.buildingCount && formik.errors.buildingCount}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    shouldHighlight={errorFieldNames?.includes('# Apt Buildings') ?? false}
                  />
                ),
                '# Stories': (
                  <StyledTextField
                    name='storyCount'
                    variant='standard'
                    value={formik.values.storyCount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.storyCount && Boolean(formik.errors.storyCount)}
                    helperText={formik.touched.storyCount && formik.errors.storyCount}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    shouldHighlight={errorFieldNames?.includes('# Stories') ?? false}
                  />
                ),
                '# Units': (
                  <StyledTextField
                    name='unitCount'
                    variant='standard'
                    value={formik.values.unitCount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.unitCount && Boolean(formik.errors.unitCount)}
                    helperText={formik.touched.unitCount && formik.errors.unitCount}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    shouldHighlight={errorFieldNames?.includes('# Units') ?? false}
                  />
                ),
                'Site Acreage': (
                  <StyledTextField
                    name='siteAcreage'
                    variant='standard'
                    value={formik.values.siteAcreage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.siteAcreage && Boolean(formik.errors.siteAcreage)}
                    helperText={formik.touched.siteAcreage && formik.errors.siteAcreage}
                    InputProps={{
                      inputComponent: NumericFormatInput as any,
                    }}
                    shouldHighlight={errorFieldNames?.includes('Site Acreage') ?? false}
                  />
                ),
              }
            : {
                'Gross Building SF':
                  generalInfo.grossBuildingSF && generalInfo.grossBuildingSF !== 0
                    ? format(generalInfo.grossBuildingSF.toString(), 'number')
                    : '--',

                'Net Rentable SF':
                  generalInfo.netRentableSF && generalInfo.netRentableSF !== 0
                    ? format(generalInfo.netRentableSF.toString(), 'number')
                    : '--',
                '# Apt Buildings':
                  generalInfo.buildingCount && generalInfo.buildingCount !== 0
                    ? format(generalInfo.buildingCount.toString(), 'number')
                    : '--',
                '# Stories':
                  generalInfo.storyCount && generalInfo.storyCount !== 0 ? format(generalInfo.storyCount.toString(), 'number') : '--',
                '# Units': generalInfo.unitCount && generalInfo.unitCount !== 0 ? format(generalInfo.unitCount.toString(), 'number') : '--',
                'Site Acreage':
                  generalInfo.siteAcreage && generalInfo.siteAcreage !== 0 ? format(generalInfo.siteAcreage.toString(), 'number') : '--',
              }
        }
        errorFields={errorFieldNames}
      />
    </form>
  );
};

export default React.memo(GeneralInfoCard);
